import { useState, useEffect, useRef } from 'react';
import { BarChart, Bar, LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, PieChart, Pie, Cell, Legend } from 'recharts';
import { Plus, TrendingUp, Weight, Recycle, Trash2, RefreshCw, Upload } from 'lucide-react';
import api from '../api/api';

const MONTHS = ['Ene','Feb','Mar','Abr','May','Jun','Jul','Ago','Sep','Oct','Nov','Dic'];
const MATERIAL_COLORS = { plastic_tons:'#3b82f6', metal_tons:'#6b7280', paper_cardboard_tons:'#f59e0b', glass_tons:'#10b981', compost_tons:'#84cc16', biostabilized_tons:'#8b5cf6' };
const MATERIAL_LABELS = { plastic_tons:'Plástico', metal_tons:'Metal', paper_cardboard_tons:'Papel/Cartón', glass_tons:'Vidrio', compost_tons:'Compost', biostabilized_tons:'Biestabilizado' };

export default function Operaciones() {
  const [summary, setSummary] = useState(null);
  const [tickets, setTickets] = useState([]);
  const [tab, setTab] = useState('produccion');
  const [showModal, setShowModal] = useState(false);
  const [showTicketModal, setShowTicketModal] = useState(false);
  const [form, setForm] = useState({ month: new Date().getMonth()+1, year: new Date().getFullYear(), total_input_tons:0, biostabilized_tons:0, compost_tons:0, plastic_tons:0, metal_tons:0, paper_cardboard_tons:0, glass_tons:0, organic_tons:0, rejected_tons:0, notes:'' });
  const [ticketForm, setTicketForm] = useState({ ticket_number:'', vehicle_plate:'', driver_name:'', waste_type:'RSU doméstico', gross_weight:0, tare_weight:0, net_weight:0, origin:'', date: new Date().toISOString().split('T')[0] });
  const [saving, setSaving] = useState(false);
  const [importing, setImporting] = useState(false);
  const [importResult, setImportResult] = useState(null);
  const fileInputRef = useRef(null);

  const load = () => {
    Promise.all([api.get('/operations/summary'), api.get('/operations/weighing?limit=50')])
      .then(([s, t]) => { setSummary(s.data); setTickets(t.data); });
  };

  useEffect(() => { load(); }, []);

  const pieData = summary ? Object.entries(MATERIAL_LABELS).map(([k,l]) => ({name: l, value: summary[`total_${k.replace('_tons','_tons')}`] || (summary[`total_${k}`] || 0)})).filter(d => d.value > 0) : [];
  const pieColors = Object.values(MATERIAL_COLORS);

  const handleSave = async (e) => {
    e.preventDefault(); setSaving(true);
    try { const d = {...form}; Object.keys(d).forEach(k => { if(typeof d[k]==='string' && !isNaN(d[k]) && k!=='notes') d[k]=parseFloat(d[k])||0; }); await api.post('/operations/production', d); setShowModal(false); load(); } finally { setSaving(false); }
  };

  const handleSaveTicket = async (e) => {
    e.preventDefault(); setSaving(true);
    try { const d = {...ticketForm, gross_weight:parseFloat(ticketForm.gross_weight)||0, tare_weight:parseFloat(ticketForm.tare_weight)||0, net_weight:parseFloat(ticketForm.gross_weight||0)-parseFloat(ticketForm.tare_weight||0) }; await api.post('/operations/weighing', d); setShowTicketModal(false); load(); } finally { setSaving(false); }
  };

  const handleImportCSV = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setImporting(true); setImportResult(null);
    try {
      const fd = new FormData(); fd.append('file', file);
      const res = await api.post('/operations/weighing/import-csv', fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      setImportResult(res.data);
      load();
    } catch (err) {
      setImportResult({ error: err?.response?.data?.detail || 'Error importando archivo' });
    } finally { setImporting(false); if(fileInputRef.current) fileInputRef.current.value=''; }
  };

  return (
    <div className="p-6 md:p-8 page-enter">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-900" style={{fontFamily:'Barlow, sans-serif'}}>Operaciones de Planta</h1>
          <p className="text-sm text-gray-500 mt-0.5">Producción, balance de materiales y pesaje</p>
        </div>
        <div className="flex gap-2">
          <button onClick={load} className="p-2 border border-gray-200 rounded-lg hover:bg-gray-50 text-gray-500"><RefreshCw size={16}/></button>
          <button onClick={() => setShowModal(true)} className="flex items-center gap-2 bg-[#16a34a] hover:bg-[#15803d] text-white text-sm font-medium px-4 py-2 rounded-lg" data-testid="add-production-btn"><Plus size={16}/> Registro Mensual</button>
        </div>
      </div>

      {/* KPI Strip */}
      {summary && (
        <div className="grid grid-cols-2 lg:grid-cols-5 gap-3 mb-6">
          {[
            { l:'Entrada Total', v:`${summary.total_input_tons?.toLocaleString('es-ES')} t`, c:'#16a34a' },
            { l:'Biestabilizado', v:`${summary.total_biostabilized_tons?.toLocaleString('es-ES')} t`, c:'#8b5cf6' },
            { l:'Compost', v:`${summary.total_compost_tons?.toLocaleString('es-ES')} t`, c:'#84cc16' },
            { l:'Tasa Valorización', v:`${summary.avg_recovery_rate?.toFixed(1)}%`, c:'#3b82f6' },
            { l:'Tasa Rechazo', v:`${summary.avg_rejection_rate?.toFixed(1)}%`, c:'#ef4444' },
          ].map(s => (
            <div key={s.l} className="bg-white rounded-xl border border-gray-200 p-4 shadow-sm">
              <p className="text-xs text-gray-500">{s.l}</p>
              <p className="text-xl font-bold mt-1" style={{fontFamily:'Barlow, sans-serif', color: s.c}}>{s.v}</p>
            </div>
          ))}
        </div>
      )}

      {/* Tabs */}
      <div className="flex gap-1 mb-4 border-b border-gray-200">
        {[{k:'produccion', l:'Producción Mensual'},{k:'tasas', l:'Tasa Valorización'},{k:'materiales', l:'Balance por Materiales'},{k:'tickets', l:`Tickets Pesaje (${tickets.length})`}].map(t => (
          <button key={t.k} onClick={() => setTab(t.k)} className={`px-4 py-2 text-sm font-medium border-b-2 -mb-px transition-colors ${tab===t.k?'border-[#16a34a] text-[#16a34a]':'border-transparent text-gray-500 hover:text-gray-700'}`}>{t.l}</button>
        ))}
      </div>

      {tab === 'produccion' && (
        <div className="space-y-4">
          {summary?.monthly_data?.length > 0 && (
            <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-6">
              <h3 className="text-sm font-semibold text-gray-900 mb-4" style={{fontFamily:'Barlow, sans-serif'}}>Entradas de Residuos - {new Date().getFullYear()}</h3>
              <ResponsiveContainer width="100%" height={200}>
                <BarChart data={summary.monthly_data} margin={{left:-20}}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0"/>
                  <XAxis dataKey="month" tickFormatter={m => MONTHS[m-1]} tick={{fontSize:11,fill:'#9ca3af'}}/>
                  <YAxis tick={{fontSize:11,fill:'#9ca3af'}}/>
                  <Tooltip formatter={(v,n) => [`${v} t`, n==='total_input_tons'?'Entrada':n==='rejected_tons'?'Rechazo':'Valorizado']} contentStyle={{fontSize:12,borderRadius:8}}/>
                  <Bar dataKey="total_input_tons" name="total_input_tons" fill="#16a34a" radius={[4,4,0,0]}/>
                  <Bar dataKey="rejected_tons" name="rejected_tons" fill="#ef4444" radius={[4,4,0,0]}/>
                </BarChart>
              </ResponsiveContainer>
            </div>
          )}
          <div className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden">
            <table className="w-full text-sm">
              <thead><tr className="bg-gray-50 border-b border-gray-200">
                {['Mes','Entrada (t)','Biestab. (t)','Compost (t)','Plástico (t)','Metal (t)','Papel (t)','Rechazo (t)','Valorización %','Rechazo %','Notas'].map(h=>(
                  <th key={h} className="text-left px-3 py-2.5 text-xs font-semibold text-gray-500 uppercase whitespace-nowrap">{h}</th>
                ))}
              </tr></thead>
              <tbody className="divide-y divide-gray-100">
                {(!summary?.monthly_data || summary.monthly_data.length===0) ? (
                  <tr><td colSpan={11} className="px-4 py-8 text-center text-gray-400 text-sm">Sin registros. Pulse "Registro Mensual" para añadir.</td></tr>
                ) : summary.monthly_data.map(r => (
                  <tr key={r.id} className="table-row-hover" data-testid={`prod-row-${r.month}`}>
                    <td className="px-3 py-2.5 font-medium text-gray-900">{MONTHS[r.month-1]} {r.year}</td>
                    <td className="px-3 py-2.5 font-mono text-right">{r.total_input_tons?.toLocaleString('es-ES')}</td>
                    <td className="px-3 py-2.5 font-mono text-right text-purple-700">{r.biostabilized_tons}</td>
                    <td className="px-3 py-2.5 font-mono text-right text-green-700">{r.compost_tons}</td>
                    <td className="px-3 py-2.5 font-mono text-right text-blue-600">{r.plastic_tons}</td>
                    <td className="px-3 py-2.5 font-mono text-right text-gray-600">{r.metal_tons}</td>
                    <td className="px-3 py-2.5 font-mono text-right text-yellow-600">{r.paper_cardboard_tons}</td>
                    <td className="px-3 py-2.5 font-mono text-right text-red-600">{r.rejected_tons}</td>
                    <td className="px-3 py-2.5 text-right"><span className={`font-semibold text-xs px-1.5 py-0.5 rounded ${r.recovery_rate>=40?'bg-green-100 text-green-800':r.recovery_rate>=30?'bg-yellow-100 text-yellow-800':'bg-red-100 text-red-800'}`}>{r.recovery_rate?.toFixed(1)}%</span></td>
                    <td className="px-3 py-2.5 text-right"><span className={`font-semibold text-xs px-1.5 py-0.5 rounded ${r.rejection_rate<=50?'bg-green-100 text-green-800':r.rejection_rate<=60?'bg-yellow-100 text-yellow-800':'bg-red-100 text-red-800'}`}>{r.rejection_rate?.toFixed(1)}%</span></td>
                    <td className="px-3 py-2.5 text-xs text-gray-400 max-w-xs truncate">{r.notes||'—'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {tab === 'tasas' && (
        <div className="space-y-4">
          {summary?.monthly_data?.length > 0 ? (
            <>
              <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-6">
                <h3 className="text-sm font-semibold text-gray-900 mb-1" style={{fontFamily:'Barlow, sans-serif'}}>Evolución Tasa de Valorización - {new Date().getFullYear()}</h3>
                <p className="text-xs text-gray-400 mb-4">Porcentaje mensual de residuos valorizados sobre el total</p>
                <ResponsiveContainer width="100%" height={220}>
                  <LineChart data={[...summary.monthly_data].sort((a,b)=>a.month-b.month)} margin={{left:-10, right:10}}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0"/>
                    <XAxis dataKey="month" tickFormatter={m => MONTHS[m-1]} tick={{fontSize:11,fill:'#9ca3af'}}/>
                    <YAxis domain={[0,100]} tickFormatter={v=>`${v}%`} tick={{fontSize:11,fill:'#9ca3af'}}/>
                    <Tooltip formatter={(v,n) => [`${v?.toFixed(1)}%`, n==='recovery_rate'?'Tasa Valorización':'Tasa Rechazo']} contentStyle={{fontSize:12,borderRadius:8}}/>
                    <Line type="monotone" dataKey="recovery_rate" name="recovery_rate" stroke="#16a34a" strokeWidth={2.5} dot={{r:4,fill:'#16a34a'}} activeDot={{r:6}}/>
                    <Line type="monotone" dataKey="rejection_rate" name="rejection_rate" stroke="#ef4444" strokeWidth={2.5} dot={{r:4,fill:'#ef4444'}} strokeDasharray="5 5"/>
                    <Legend formatter={v => v==='recovery_rate'?'Valorización (%)':'Rechazo (%)'} wrapperStyle={{fontSize:11}}/>
                  </LineChart>
                </ResponsiveContainer>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                {[
                  { label:'Mejor Mes', value: (() => { const best = [...summary.monthly_data].sort((a,b)=>b.recovery_rate-a.recovery_rate)[0]; return best ? `${MONTHS[best.month-1]}: ${best.recovery_rate?.toFixed(1)}%` : '—'; })(), color:'#16a34a' },
                  { label:'Peor Mes', value: (() => { const worst = [...summary.monthly_data].sort((a,b)=>a.recovery_rate-b.recovery_rate)[0]; return worst ? `${MONTHS[worst.month-1]}: ${worst.recovery_rate?.toFixed(1)}%` : '—'; })(), color:'#ef4444' },
                  { label:'Media Anual', value: `${summary.avg_recovery_rate?.toFixed(1)}%`, color:'#3b82f6' },
                ].map(s => (
                  <div key={s.label} className="bg-white rounded-xl border border-gray-200 p-4 shadow-sm">
                    <p className="text-xs text-gray-500">{s.label}</p>
                    <p className="text-xl font-bold mt-1" style={{fontFamily:'Barlow, sans-serif', color: s.color}}>{s.value}</p>
                  </div>
                ))}
              </div>
            </>
          ) : (
            <div className="bg-white rounded-xl border border-gray-200 p-12 text-center text-gray-400 text-sm">Sin datos de producción. Añade registros mensuales para ver la evolución.</div>
          )}
        </div>
      )}

      {tab === 'materiales' && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-6">
            <h3 className="text-sm font-semibold text-gray-900 mb-4" style={{fontFamily:'Barlow, sans-serif'}}>Distribución por Material ({new Date().getFullYear()})</h3>
            <ResponsiveContainer width="100%" height={250}>
              <PieChart>
                <Pie data={pieData} cx="50%" cy="50%" outerRadius={90} dataKey="value" label={({name,percent}) => `${name} ${(percent*100).toFixed(0)}%`} labelLine={false} fontSize={11}>
                  {pieData.map((_, i) => <Cell key={i} fill={pieColors[i % pieColors.length]}/>)}
                </Pie>
                <Tooltip formatter={v => [`${v} t`]}/>
              </PieChart>
            </ResponsiveContainer>
          </div>
          <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-6">
            <h3 className="text-sm font-semibold text-gray-900 mb-4" style={{fontFamily:'Barlow, sans-serif'}}>Resumen Anual de Materiales</h3>
            <div className="space-y-3">
              {summary && Object.entries(MATERIAL_LABELS).map(([k,l]) => {
                const val = summary[`total_${k}`] || 0;
                const maxVal = Math.max(...Object.entries(MATERIAL_LABELS).map(([mk]) => summary[`total_${mk}`]||0), 1);
                return (
                  <div key={k}>
                    <div className="flex justify-between text-xs mb-1"><span className="text-gray-700">{l}</span><span className="font-mono font-medium text-gray-900">{val.toLocaleString('es-ES')} t</span></div>
                    <div className="h-1.5 bg-gray-100 rounded-full"><div className="h-full rounded-full transition-all" style={{width:`${(val/maxVal)*100}%`, backgroundColor: MATERIAL_COLORS[k]}}/></div>
                  </div>
                );
              })}
              {summary && (
                <div className="pt-3 border-t border-gray-200 flex justify-between text-sm">
                  <span className="text-gray-600">Rechazo total</span>
                  <span className="font-mono font-bold text-red-600">{summary.total_rejected_tons?.toLocaleString('es-ES')} t</span>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {tab === 'tickets' && (
        <div className="space-y-3">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              {importResult && (
                <span className={`text-xs px-2 py-1 rounded ${importResult.error ? 'bg-red-50 text-red-700' : 'bg-green-50 text-green-700'}`} data-testid="import-result">
                  {importResult.error ? importResult.error : `${importResult.inserted} tickets importados`}
                  {importResult.errors?.length > 0 && ` (${importResult.errors.length} errores)`}
                </span>
              )}
            </div>
            <div className="flex gap-2">
              <input type="file" accept=".csv,.xlsx,.xls" ref={fileInputRef} onChange={handleImportCSV} className="hidden" data-testid="import-weighing-file"/>
              <button onClick={() => fileInputRef.current?.click()} disabled={importing} className="flex items-center gap-2 border border-gray-300 hover:bg-gray-50 text-gray-700 text-sm font-medium px-3 py-2 rounded-lg" data-testid="import-weighing-btn">
                <Upload size={14}/> {importing ? 'Importando...' : 'Importar CSV/Excel'}
              </button>
              <button onClick={() => setShowTicketModal(true)} className="flex items-center gap-2 bg-[#16a34a] hover:bg-[#15803d] text-white text-sm font-medium px-3 py-2 rounded-lg" data-testid="add-ticket-btn"><Plus size={15}/> Nuevo Ticket</button>
            </div>
          </div>
          <div className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden">
            <table className="w-full text-sm">
              <thead><tr className="bg-gray-50 border-b border-gray-200">
                {['Ticket','Fecha','Matrícula','Conductor','Tipo Residuo','Bruto (kg)','Tara (kg)','Neto (kg)','Origen'].map(h=>(
                  <th key={h} className="text-left px-3 py-2.5 text-xs font-semibold text-gray-500 uppercase whitespace-nowrap">{h}</th>
                ))}
              </tr></thead>
              <tbody className="divide-y divide-gray-100">
                {tickets.length===0 ? <tr><td colSpan={9} className="px-4 py-8 text-center text-gray-400 text-sm">Sin tickets registrados</td></tr>
                : tickets.map(t => (
                  <tr key={t.id} className="table-row-hover" data-testid={`ticket-row-${t.id}`}>
                    <td className="px-3 py-2.5 font-mono text-xs text-gray-600">{t.ticket_number}</td>
                    <td className="px-3 py-2.5 text-xs text-gray-600">{t.date}</td>
                    <td className="px-3 py-2.5 font-mono text-xs font-semibold text-gray-900">{t.vehicle_plate}</td>
                    <td className="px-3 py-2.5 text-xs text-gray-700">{t.driver_name||'—'}</td>
                    <td className="px-3 py-2.5 text-xs text-gray-700">{t.waste_type}</td>
                    <td className="px-3 py-2.5 font-mono text-right text-xs">{t.gross_weight?.toLocaleString('es-ES')}</td>
                    <td className="px-3 py-2.5 font-mono text-right text-xs">{t.tare_weight?.toLocaleString('es-ES')}</td>
                    <td className="px-3 py-2.5 font-mono text-right text-xs font-bold text-[#16a34a]">{t.net_weight?.toLocaleString('es-ES')}</td>
                    <td className="px-3 py-2.5 text-xs text-gray-500">{t.origin||'—'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Production Modal */}
      {showModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-2xl w-full max-w-2xl max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between p-5 border-b sticky top-0 bg-white">
              <h2 className="text-base font-bold text-gray-900" style={{fontFamily:'Barlow, sans-serif'}}>Registro Mensual de Producción</h2>
              <button onClick={() => setShowModal(false)} className="text-gray-400 hover:text-gray-600 text-xl">&times;</button>
            </div>
            <form onSubmit={handleSave} className="p-5 space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div><label className="block text-xs font-medium text-gray-700 mb-1">Mes</label>
                  <select value={form.month} onChange={e=>setForm({...form,month:parseInt(e.target.value)})} className="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg focus:ring-2 focus:ring-[#16a34a] focus:outline-none bg-white">
                    {MONTHS.map((m,i) => <option key={i+1} value={i+1}>{m}</option>)}
                  </select>
                </div>
                <div><label className="block text-xs font-medium text-gray-700 mb-1">Año</label>
                  <select value={form.year} onChange={e=>setForm({...form,year:parseInt(e.target.value)})} className="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg focus:ring-2 focus:ring-[#16a34a] focus:outline-none bg-white">
                    {[2026,2025,2024,2023].map(y=><option key={y} value={y}>{y}</option>)}
                  </select>
                </div>
              </div>
              <div className="bg-gray-50 rounded-lg p-4">
                <p className="text-xs font-semibold text-gray-600 uppercase tracking-wider mb-3">Entradas / Toneladas</p>
                <div className="grid grid-cols-2 gap-3">
                  {[{l:'Total Entrada (t)',k:'total_input_tons'},{l:'Biestabilizado (t)',k:'biostabilized_tons'},{l:'Compost (t)',k:'compost_tons'},{l:'Plástico (t)',k:'plastic_tons'},{l:'Metal (t)',k:'metal_tons'},{l:'Papel/Cartón (t)',k:'paper_cardboard_tons'},{l:'Vidrio (t)',k:'glass_tons'},{l:'Fracción Orgánica (t)',k:'organic_tons'},{l:'Rechazo (t)',k:'rejected_tons'}].map(f=>(
                    <div key={f.k}><label className="block text-xs font-medium text-gray-600 mb-1">{f.l}</label>
                      <input type="number" step="0.01" min="0" value={form[f.k]} onChange={e=>setForm({...form,[f.k]:e.target.value})} className="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg focus:ring-2 focus:ring-[#16a34a] focus:outline-none font-mono"/>
                    </div>
                  ))}
                </div>
              </div>
              <div><label className="block text-xs font-medium text-gray-700 mb-1">Observaciones</label>
                <textarea value={form.notes} onChange={e=>setForm({...form,notes:e.target.value})} rows={2} className="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg focus:ring-2 focus:ring-[#16a34a] focus:outline-none resize-none"/>
              </div>
              <div className="flex justify-end gap-2"><button type="button" onClick={()=>setShowModal(false)} className="px-4 py-2 text-sm border border-gray-200 rounded-lg hover:bg-gray-50">Cancelar</button>
                <button type="submit" disabled={saving} className="px-4 py-2 text-sm bg-[#16a34a] text-white rounded-lg hover:bg-[#15803d]" data-testid="save-production-btn">{saving?'Guardando...':'Guardar'}</button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Ticket Modal */}
      {showTicketModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-2xl w-full max-w-lg">
            <div className="flex items-center justify-between p-5 border-b">
              <h2 className="text-base font-bold text-gray-900" style={{fontFamily:'Barlow, sans-serif'}}>Nuevo Ticket de Pesaje</h2>
              <button onClick={() => setShowTicketModal(false)} className="text-gray-400 hover:text-gray-600 text-xl">&times;</button>
            </div>
            <form onSubmit={handleSaveTicket} className="p-5 grid grid-cols-2 gap-4">
              {[{l:'Nº Ticket',k:'ticket_number',req:true},{l:'Matrícula',k:'vehicle_plate',req:true},{l:'Conductor',k:'driver_name'},{l:'Origen',k:'origin'},{l:'Fecha',k:'date',type:'date',req:true}].map(f=>(
                <div key={f.k}><label className="block text-xs font-medium text-gray-700 mb-1">{f.l}</label>
                  <input type={f.type||'text'} value={ticketForm[f.k]} required={f.req} onChange={e=>setTicketForm({...ticketForm,[f.k]:e.target.value})} className="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg focus:ring-2 focus:ring-[#16a34a] focus:outline-none"/>
                </div>
              ))}
              <div><label className="block text-xs font-medium text-gray-700 mb-1">Tipo Residuo</label>
                <select value={ticketForm.waste_type} onChange={e=>setTicketForm({...ticketForm,waste_type:e.target.value})} className="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg focus:ring-2 focus:ring-[#16a34a] focus:outline-none bg-white">
                  {['RSU doméstico','Fracción orgánica','Residuos voluminosos','Podas y jardines','Residuos comerciales','Otros'].map(t=><option key={t} value={t}>{t}</option>)}
                </select>
              </div>
              {[{l:'Peso Bruto (kg)',k:'gross_weight'},{l:'Tara (kg)',k:'tare_weight'}].map(f=>(
                <div key={f.k}><label className="block text-xs font-medium text-gray-700 mb-1">{f.l}</label>
                  <input type="number" min="0" value={ticketForm[f.k]} onChange={e=>setTicketForm({...ticketForm,[f.k]:e.target.value})} className="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg focus:ring-2 focus:ring-[#16a34a] focus:outline-none font-mono"/>
                </div>
              ))}
              <div className="col-span-2 flex justify-end gap-2 pt-2">
                <button type="button" onClick={()=>setShowTicketModal(false)} className="px-4 py-2 text-sm border border-gray-200 rounded-lg hover:bg-gray-50">Cancelar</button>
                <button type="submit" disabled={saving} className="px-4 py-2 text-sm bg-[#16a34a] text-white rounded-lg" data-testid="save-ticket-btn">{saving?'Guardando...':'Registrar'}</button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
